import { logError } from "@/lib/log";
import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Send, MessageSquare } from "lucide-react";
import { useIsPremium, PremiumUpsell } from "@/components/PremiumGate";

function renderMarkdown(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="font-mono text-xs bg-[#EAE7DD] px-1">$1</code>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul class="list-disc pl-5 space-y-1 my-2">$&</ul>')
    .replace(/\n\n/g, '</p><p class="mt-2">')
    .replace(/\n/g, '<br/>');
}

const SUGGESTIONS = [
  "How much did I spend this month?",
  "Where can I cut back?",
  "Log ₹450 for groceries yesterday",
  "Am I on track with my budgets?",
];

function SessionList({ sessions, activeId, onOpen, onDelete }) {
  return (
    <aside className="lg:col-span-1 border border-[#D8D4C7] bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mb-3 flex items-center gap-2">
        <MessageSquare className="w-3 h-3" /> Conversations
      </div>
      {sessions.length === 0 ? (
        <div className="text-sm text-[#6C7565] italic py-4">None yet.</div>
      ) : (
        <ul className="space-y-1" data-testid="chat-session-list">
          {sessions.map((s) => (
            <li key={s.id} className="group">
              <div className={`flex items-center justify-between gap-2 p-2 border ${activeId === s.id ? "border-[#1A1F16] bg-[#EAE7DD]" : "border-transparent hover:border-[#D8D4C7]"}`}>
                <button
                  data-testid={`chat-session-${s.id}`}
                  onClick={() => onOpen(s.id)}
                  className="flex-1 text-left text-sm truncate"
                >{s.title}</button>
                <button onClick={() => onDelete(s.id)} data-testid={`chat-delete-${s.id}`} className="opacity-0 group-hover:opacity-100 text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function ChatMessage({ message }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex gap-3 fade-in ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`shrink-0 w-8 h-8 flex items-center justify-center font-mono text-[10px] uppercase tracking-widest ${
        isUser ? "bg-[#1A1F16] text-[#F7F5F0]" : "bg-[#A67C00] text-white"
      }`}>
        {isUser ? "You" : "AI"}
      </div>
      <div className={`max-w-[80%] p-4 ${isUser ? "bg-[#EAE7DD]" : "bg-[#FDFBF7] border border-[#D8D4C7]"}`}>
        <div
          className="font-serif text-lg leading-relaxed [&_p]:mt-2 [&_p:first-child]:mt-0 [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize("<p>" + renderMarkdown(message.content) + "</p>") }}
        />
      </div>
    </div>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex gap-3 fade-in">
      <div className="shrink-0 w-8 h-8 flex items-center justify-center font-mono text-[10px] uppercase tracking-widest bg-[#A67C00] text-white">AI</div>
      <div className="bg-[#FDFBF7] border border-[#D8D4C7] p-4">
        <div className="flex gap-1.5" aria-label="thinking">
          <span className="w-1.5 h-1.5 rounded-full bg-[#A67C00] animate-pulse" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-[#A67C00] animate-pulse" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-[#A67C00] animate-pulse" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function EmptyChat({ onSuggest }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-16">
      <Sparkles className="w-8 h-8 text-[#A67C00] mb-4" />
      <h3 className="font-serif text-3xl tracking-tight">Ask your ledger anything.</h3>
      <p className="text-[#6C7565] text-sm mt-2 max-w-md">I've read your accounts, budgets and transactions. I can also record new entries for you. Try one of these:</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6 max-w-xl w-full">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={s}
            data-testid={`suggestion-${i}`}
            onClick={() => onSuggest(s)}
            className="text-left border border-[#D8D4C7] p-3 text-sm hover:border-[#1A1F16] hover:bg-[#FDFBF7] transition-colors"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatComposer({ input, setInput, sending, onSend }) {
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSend(); }}
      className="border-t border-[#D8D4C7] p-4 flex items-end gap-3"
      data-testid="chat-input-form"
    >
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
        }}
        placeholder="Ask about your finances… (Shift+Enter for newline)"
        rows={1}
        data-testid="chat-input"
        className="flex-1 bg-transparent border-b border-[#1A1F16] py-2 resize-none focus:outline-none focus:border-[#A67C00] font-sans"
      />
      <button
        type="submit"
        disabled={!input.trim() || sending}
        data-testid="chat-send-btn"
        className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#2C3628] disabled:opacity-40 flex items-center gap-2"
      >
        <Send className="w-3.5 h-3.5" /> Send
      </button>
    </form>
  );
}

/** All chat state + API interactions for the Ask page. */
function useChat(enabled = true) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/ai/chat/sessions");
      setSessions(data);
    } catch (e) {
      logError("Chat sessions load failed", e);
    }
  };
  useEffect(() => { if (enabled) loadSessions(); }, [enabled]);

  const openSession = async (id) => {
    setActiveId(id);
    try {
      const { data } = await api.get(`/ai/chat/sessions/${id}`);
      setMessages(data.messages);
    } catch { toast.error("Couldn't open conversation"); }
  };

  const newSession = () => {
    setActiveId(null);
    setMessages([]);
    setInput("");
  };

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    setSending(true);
    // Optimistic user message
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: value, ts: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat/message", { session_id: activeId, text: value });
      setActiveId(data.session_id);
      setMessages((m) => {
        const withoutTemp = m.filter((x) => x.id !== tempUser.id);
        return [...withoutTemp, data.user_message, data.assistant_message];
      });
      if (data.created_transaction) {
        toast.success("Transaction recorded in your ledger");
        window.dispatchEvent(new CustomEvent("voice-expense:saved"));
      }
      loadSessions();
    } catch {
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
      toast.error("Message failed");
    } finally {
      setSending(false);
    }
  };

  const removeSession = async (id) => {
    await api.delete(`/ai/chat/sessions/${id}`);
    if (activeId === id) newSession();
    loadSessions();
  };

  return { sessions, activeId, messages, input, setInput, sending, openSession, newSession, send, removeSession };
}

export default function Ask() {
  const isPremium = useIsPremium();
  const chat = useChat(isPremium);
  const { sessions, activeId, messages, input, setInput, sending, openSession, newSession, send, removeSession } = chat;
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!isPremium) {
    return (
      <div className="space-y-6">
        <div className="border-b border-[#1A1F16] pb-6">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VII</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Ask.</h2>
        </div>
        <PremiumUpsell feature="The AI assistant" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A67C00]">Section VII · Powered by GPT-5.4 Mini</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Ask.</h2>
          <p className="font-serif text-lg text-[#6C7565] mt-2 max-w-2xl">A conversation with your household ledger.</p>
        </div>
        <button
          data-testid="new-chat-btn"
          onClick={newSession}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> New chat
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 min-h-[540px]">
        <SessionList sessions={sessions} activeId={activeId} onOpen={openSession} onDelete={removeSession} />

        <section className="lg:col-span-3 border border-[#D8D4C7] bg-white flex flex-col" data-testid="chat-area">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5 max-h-[540px]">
            {messages.length === 0 ? (
              <EmptyChat onSuggest={send} />
            ) : (
              messages.map((m) => <ChatMessage key={m.id} message={m} />)
            )}
            {sending && <ThinkingIndicator />}
          </div>
          <ChatComposer input={input} setInput={setInput} sending={sending} onSend={send} />
        </section>
      </div>
    </div>
  );
}
